import { useEffect, useRef, useState } from "react";
import { useImmer } from "use-immer";
import SimpleReactValidator from "simple-react-validator";
import {
  getTenantSocialSettingsApi,
  updateTenantSocialSettingsApi,
} from "../apis/socialApis";
import { errorMessage, successMessage } from "../../../utils";

export const useSocialConfig = () => {
  const [, setForceUpdate] = useState(0);
  const [state, setState] = useImmer({
    formData: {
      facebook: "",
      twitter: "",
      instagram: "",
      tiktok: "",
      youtube: "",
    },
    isLoading: true,
    isSubmitting: false,
    hasChanges: false,
  });

  const formValidator = useRef(
    new SimpleReactValidator({
      autoForceUpdate: { forceUpdate: setForceUpdate },
    }),
  );

  useEffect(() => {
    fetchSocialSettings();
  }, []);

  const fetchSocialSettings = async () => {
    try {
      setState((draft) => {
        draft.isLoading = true;
      });

      const response = await getTenantSocialSettingsApi();

      if (response?.success) {
        setState((draft) => {
          draft.formData = response.data;
        });
      } else {
        throw new Error(
          response.message || "Failed to fetch tenant social settings",
        );
      }
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error?.message ||
        "Failed to fetch tenant social settings";
      errorMessage(message);
    } finally {
      setState((draft) => {
        draft.isLoading = false;
      });
    }
  };

  const updateSocialSettings = async (payload) => {
    try {
      setSubmitting(true);

      const response = await updateTenantSocialSettingsApi(payload);

      if (response?.success) {
        setState((draft) => {
          draft.formData = response.data;
          draft.hasChanges = false;
        });
        successMessage(response.message);
        fetchSocialSettings();
      } else {
        throw new Error(
          response.message || "Failed to update tenant social settings",
        );
      }
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error?.message ||
        "Failed to update tenant social settings";
      errorMessage(message);
    } finally {
      setSubmitting(false);
    }
  };

  const setSubmitting = (submitting) => {
    setState((draft) => {
      draft.isSubmitting = submitting;
    });
  };

  const onChange = (event) => {
    const { name, value } = event.target;
    setState((draft) => {
      draft.formData[name] = value;
      draft.hasChanges = true;
    });
  };

  const onSubmit = () => {
    if (formValidator.current.allValid()) {
      updateSocialSettings(state.formData);
    } else {
      formValidator.current.showMessages();
      setForceUpdate((previous) => previous + 1);
    }
  };

  return { state, formValidator, onChange, onSubmit };
};
